import React from 'react';
import { Menu } from 'antd';
import PropTypes from 'prop-types';

import {
    BarChartOutlined,
    BulbOutlined,
    GlobalOutlined,
    LaptopOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';

/**
 * Section definitions with their templates.
 * Exported so other components can access templates.
 */
export const EDITOR_SECTIONS = [
    {
        key: 'technology',
        icon: <LaptopOutlined />,
        label: 'Technology',
        children: [
            {
                key: 'technology.tradingTools',
                label: 'Trading Tools',
                template:
                    'Trading Platform: \nTrading Scanners: \nBroker: \nTrading Desk: \nTrading Community: '
            },
            {
                key: 'technology.hotKeys',
                label: 'HotKeys',
                template: '· '
            },
            {
                key: 'technology.journalCriteria',
                label: 'Journal Criteria',
                template:
                    'Journal Tools:\n· Microsoft products: \n· TraderVue:\nTrade Analysis:\n· '
            }
        ]
    },
    {
        key: 'strategy',
        icon: <BarChartOutlined />,
        label: 'Strategy',
        children: [
            {
                key: 'strategy.tradingRules',
                label: 'Trading Rules',
                // eslint-disable-next-line max-len -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
                template:
                    'Rule 1: Day Trading is not a strategy to get rich quickly\nRule 2: Day Trading is not easy. It is a serious business, and you should treat it as such\n\nRule 3: Day Traders do not hold positions overnight. If necessary, you must sell with a loss to make sure you do not hold onto any stock overnight\n\nRule 4: Always ask, "Is the stock moving because the overall market is moving, or is it moving because it has a unique fundament catalyst."\n\nRule 5: Success in day trading comes from risk management - finding low-risk entries with a high potential reward. The minimum win:lose ratio for me is 2:1\n\nRule 6: Your broker will buy and sell stocks for you at the Exchange. Your only job as a day trader is to manage risk. You cannot be a successful day trader without excellent risk management skills, even if you are the master of many effective strategies.\n\nRule 7: Retail traders trade only Stocks in Play, high relative volume stocks that have fundamental catalyst and are being traded regardless of the overall market.\n\nRule 8: Experienced traders are like guerrilla soldiers. They jump out at just the right time, take their profit and get out.\n\nRule 9: Hollow candlesticks, where the close is greater than the open, indicate buying pressure. Filled candlesticks, where the close is less than the open, indicate selling pressure.\n\nRule 10: Indicators only indicate; they should not be allowed to dictate.\n\nRule 11: Profitable trading does not involve emotion. If you are an emotional trader, you will lose your money.\n\nRule 12: '
            },
            {
                key: 'strategy.tradingFramework',
                label: 'Trading Framework',
                template:
                    'Trading Tenets:\n· \n\nDaily Stock Selection Criteria:\n· \n\nDaily Max Loss: \n\nDaily Profit Goal:\n\nMax 30 Day Loss: \n\nExchanges: \n\nAdditional:\n· '
            }
        ]
    },
    {
        key: 'psychology',
        icon: <BulbOutlined />,
        label: 'Psychology',
        children: [
            {
                key: 'psychology.tradingGoals',
                label: 'Trading Goals',
                // eslint-disable-next-line max-len -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
                template:
                    'GOALS (Specific, Measurable, Attainable, Relevant, Time-Based)\n\nGoal 1: SMART Goals\nS: Specific\nM: Measurable\nA: Achievable\nR: Realistic\nT: Timed\n\nTimeframe to achieve\nHow will you Track\n\nOutcome Goals\n· These goals are targets that can be influenced by factors outside of your control\n· \n· \n\nPerformance Goals\n· These goals are used to help you improve your own performance in specific areas\n· \n· \n\nProcess Goals\n· These goals focus on strategy, emotional regulation, daily habits and any other processes you must go through to trade successfully or to meet your performance and outcome goals.\n· \n· '
            },
            {
                key: 'psychology.traderPersonalityProfile',
                label: 'Trader Personality Profile',
                template:
                    '· Complete the Trader Personality Profile: https://aware-profile.daytradingpsychology.com/'
            },
            {
                key: 'psychology.mentalTrainingPlan',
                label: 'Mental Training Plan',
                template:
                    'Performance Profile:\n\nGoals:\n\n(Use the SMART Goals structure from the Trading Goals section or create your custom mental training routine here)'
            }
        ]
    },
    {
        key: 'tradeBookEdge',
        icon: <ThunderboltOutlined />,
        label: 'TradeBook Edge',
        children: [
            {
                key: 'tradeBookEdge.tradingEdgeDefined',
                label: 'Trading Edge Defined',
                template: 'Edge Defined\nTradeBook Edge 1: \nTradeBook Edge 2: '
            }
        ]
    },
    {
        key: 'global',
        icon: <GlobalOutlined />,
        label: 'Global TradeBook'
    }
];

/**
 * Get template for a given section key
 */
export const getTemplate = (key) => {
    let foundTemplate = '';

    EDITOR_SECTIONS.forEach((section) => {
        if (section.children) {
            const child = section.children.find((c) => c.key === key);
            if (child) {
                foundTemplate = child.template || '';
            }
        }
    });

    return foundTemplate;
};

/**
 * EditorNavigation - Menu for selecting editor sections
 */
export default function EditorNavigation({ selectedSection, onSelectSection }) {
    const handleMenuClick = ({ key }) => {
        onSelectSection(key);
    };

    return (
        <div className="editor-navigation">
            <Menu
                mode="inline"
                selectedKeys={selectedSection ? [selectedSection] : []}
                items={EDITOR_SECTIONS}
                onClick={handleMenuClick}
                style={{ border: 'none', height: '100%' }}
            />
        </div>
    );
}

EditorNavigation.propTypes = {
    selectedSection: PropTypes.string,
    onSelectSection: PropTypes.func.isRequired
};
